import {useI18n} from "vue-i18n";

// Aynı hata mesajlarının tekrar gösterilmesini engellemek için cache
const shownErrorMessages = new Map();
const ERROR_MESSAGE_COOLDOWN = 5000; // 5 saniye içinde aynı mesaj gösterilmesin

function shouldShowErrorMessage(message) {
    const now = Date.now();
    const lastShown = shownErrorMessages.get(message);

    if (lastShown && (now - lastShown) < ERROR_MESSAGE_COOLDOWN) {
        return false;
    }

    shownErrorMessages.set(message, now);

    // Eski mesajları temizle (memory leak önleme)
    if (shownErrorMessages.size > 50) {
        const oldestAllowed = now - ERROR_MESSAGE_COOLDOWN;
        for (const [msg, time] of shownErrorMessages.entries()) {
            if (time < oldestAllowed) {
                shownErrorMessages.delete(msg);
            }
        }
    }

    return true;
}

export function handleErrorResponse(error, snackbar) {
    try {
        if (error.response.data && error.response.data.errors && Object.keys(error.response.data.errors).length > 0) {
            Object.keys(error.response.data.errors).forEach(key => {
                const message = error.response.data.errors[key][0];
                if (shouldShowErrorMessage(message)) {
                    snackbar.add({
                        text: message,
                        type: 'error',
                    });
                }
            });
        } else if (error.response.data.message) {
            const message = error.response.data.message;
            if (shouldShowErrorMessage(message)) {
                snackbar.add({
                    text: message,
                    type: 'error',
                });
            }
        } else if (error.response.message) {
            const message = error.response.message;
            if (shouldShowErrorMessage(message)) {
                snackbar.add({
                    text: message,
                    type: 'error',
                });
            }
        } else {
            const message = 'Undefined error occurred.';
            if (shouldShowErrorMessage(message)) {
                snackbar.add({
                    text: message,
                    type: 'error',
                });
            }
        }
    } catch (e) {
        console.log(e)
        const message = 'Undefined error occurred.';
        if (shouldShowErrorMessage(message)) {
            snackbar.add({
                text: message,
                type: 'error',
            });
        }
    }
}

export function convertBooleanValuesToNumbers(data) {
    const convertValue = (value) => {
        if (typeof value === 'boolean') {
            return value ? 1 : 0;
        }

        if (typeof value === 'object') {
            for (const key in value) {
                if (value.hasOwnProperty(key)) {
                    value[key] = convertValue(value[key]);
                }
            }
        }
        return value;
    };

    return convertValue(data);
}

export function isQuestion(type) {
    const questionTypes = ['multiple', 'single', 'yesNo', 'number', 'text', 'grading', 'rating', 'dragAndDrop', 'fillTheBlank', 'matchType', 'swipe'];
    return questionTypes.includes(type);
}

export function stripHtmlTags(text) {
    if (!text) {
        return '';
    }

    let cleanedText = text.replace(/<[^>]+>/g, ' ');

    cleanedText = cleanedText.replace(/\s+/g, ' ').trim();

    return cleanedText;
}

export function truncate(str, n){
    if (!str) {
        return '';
    }

    const text = str.replace(/<[^>]*>/g, '');
    return (text.length > n) ? text.slice(0, n-1) + '...' : text;
}

export function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') {
        return obj;
    }

    if (obj instanceof File || obj instanceof Blob) {
        return obj; // Return file objects as-is
    }

    if (Array.isArray(obj)) {
        return obj.map(deepClone);
    }

    let cloned = {};
    for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
            cloned[key] = deepClone(obj[key]);
        }
    }
    return cloned;
}


export function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function truncateMiddle(str, n){
    if (!str) {
        return '';
    }

    if (str.length <= n) {
        return str;
    }

    const start = str.slice(0, n/2);
    const end = str.slice(-n/2);

    return start + '...' + end;
}


export function formatDateDMYHI(date) {
    const selectedDate = new Date(date);
    const options = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    };
    const formatter = new Intl.DateTimeFormat("en-GB", options);
    return formatter.format(selectedDate).replace(/\//g, '.').replace(/[/,]/g, '');
}

export function formatDateMD(isoDateString) {
    const date = new Date(isoDateString);

    const userLanguage = localStorage.getItem('userLanguage') || 'en'; // Varsayılan olarak İngilizce

    const day = date.getUTCDate();

    if (userLanguage === "tr") {
        const aylar = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
        const ay = aylar[date.getUTCMonth()];

        let suffix = '.';

        return `${day}${suffix} ${ay}`;
    } else {
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getUTCMonth()];

        let suffix = 'th';
        if (day % 10 === 1 && day !== 11) {
            suffix = 'st';
        } else if (day % 10 === 2 && day !== 12) {
            suffix = 'nd';
        } else if (day % 10 === 3 && day !== 13) {
            suffix = 'rd';
        }

        return `${month} ${day}${suffix}`;
    }
}

export function formatRelativeTime(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    const userLanguage = localStorage.getItem('userLanguage') || 'tr';
    const isTr = userLanguage === 'tr';

    if (diffInSeconds < 60) {
        return isTr ? 'birkaç saniye önce' : 'a few seconds ago';
    }

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) {
        return isTr ? `${diffInMinutes} dakika önce` : `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) {
        return isTr ? `${diffInHours} saat önce` : `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) {
        return isTr ? `${diffInDays} gün önce` : `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    }

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) {
        return isTr ? `${diffInWeeks} hafta önce` : `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
    }

    const diffInMonths = Math.floor(diffInDays / 30);
    if (diffInMonths < 12) {
        return isTr ? `${diffInMonths} ay önce` : `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
    }

    const diffInYears = Math.floor(diffInDays / 365);
    return isTr ? `${diffInYears} yıl önce` : `${diffInYears} year${diffInYears > 1 ? 's' : ''} ago`;
}

export function uuidv4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
        var r = Math.random() * 16 | 0,
            v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export function validateProgramPart(part) {
    const type = part.content?.type;

    return {
        isValid: true,
        type: 'success',
        message: ''
    };

    switch (type) {
        case 'welcomeText':
            if (!part.name) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Name is required.'
                };
            }
            break;

        case 'single':
        case 'multiple':
        case 'yesNo':
        case 'dragAndDrop':
            if (!part.name) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Name is required.'
                };
            }

            if (part.content?.options?.length < 2) {
                return {
                    isValid: true,
                    type: 'warning',
                    message: 'At least 2 options are recommended.'
                };
            }
            break;

        case 'rating':
        case 'grading':
        case 'number':
            if (!part.name) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Name is required.'
                };
            }

            if (!part.content?.settings?.limitedRange?.min) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Minimum value is required.'
                };
            }

            if (!part.content?.settings?.limitedRange?.max) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Maximum value is required.'
                };
            }
            break;

        case 'text':
            if (!part.name) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Name is required.'
                };
            }

            if (!part.content?.settings?.characterLimit?.min) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Minimum character value is required.'
                };
            }

            if (!part.content?.settings?.characterLimit?.max) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Maximum character value is required.'
                };
            }
            break;

        case 'summary':
            if (!part.description) {
                return {
                    isValid: false,
                    type: 'error',
                    message: 'Description is required.'
                };
            }

            break;

    }

    return {
        isValid: true,
        type: 'success',
        message: ''
    };
}


const TYPE_TO_NAME_MAP = {
    'multiple': 'multiple_choice',
    'single': 'single_choice',
    'yesNo': 'yes_no_question',
    'number': 'number_question',
    'text': 'text_question',
    'grading': 'grading_question',
    'rating': 'rating_question',
    'dragAndDrop': 'drag_drop_question',
    'matchType': 'match_type',
    'fillTheBlank': 'fill_blank_type',
    'welcomeText': 'welcome_text',
    'audio': 'audio_type',
    'video': 'video_type',
    'summary': 'summary_type',
    'selfChat': 'self_chat_type',
    'success': 'success_type',
    'failed': 'failed_type',
    'scenarioText': 'scenario_text',
    'agentRedirect': 'agent_redirect',
};

export const getNameFromType = (type) => {
    const { t } = useI18n();

    const translationKey = TYPE_TO_NAME_MAP[type];
    return translationKey ? t(translationKey) : "Undefined type";
};

export const colorPalette = [
    '#e6194B', '#3cb44b', '#ffe119', '#4363d8', '#f58231',
    '#911eb4', '#42d4f4', '#f032e6', '#bfef45', '#fabed4',
    '#469990', '#dcbeff', '#9A6324', '#fffac8', '#800000',
    '#aaffc3', '#808000', '#ffd8b1', '#000075', '#a9a9a9',
    '#ffffff', '#000000'
];


export const agentBuilderTools = [
    {
        name: "Edit Q/A Pair",
        key: "edit_qa",
        description: "Use this tool to edit an existing Q/A pair in the agent's Editing context.",
        usage: "//edit_qa:{'index': 'Index of the pair', 'question': 'Your question', 'answer': 'Your answer' }"
    },
    {
        name: "Add Q/A Pair",
        key: "add_qa",
        description: "Use this tool to add a new Q/A pair to the agent's Editing context.",
        usage: "//add_qa:{'question': 'Your question', 'answer': 'Your answer' }"
    },
    {
        name: "Remove Q/A Pair",
        key: "remove_qa",
        description: "Use this tool to remove an existing Q/A pair from the agent's Editing context.",
        usage: "//remove_qa:{'index': 'Index of the pair' }"
    },
    {
        name: "Edit Context",
        key: "edit_context",
        description: "Use this tool to edit the agent's Editing context.",
        usage: "//edit_context:{'context': 'Your new context' }"
    },
    {
        name: "Train Agent",
        key: "train_agent",
        description: "Use this tool to train the agent with the editing context and Q/A pairs. Do not train until user requests it.",
        usage: "//train_agent"
    },
];

// Improved helper function to parse JSON-like strings with single quotes
const parseJsonLikeString = (str) => {
    if (!str || typeof str !== 'string') return str;

    try {
        // First try parsing as-is (in case it's already valid JSON)
        return JSON.parse(str);
    } catch {
        try {
            // More robust conversion from single quotes to double quotes
            let jsonString = str.trim();

            // Remove outer single quotes if they exist
            if (jsonString.startsWith("'") && jsonString.endsWith("'")) {
                jsonString = jsonString.slice(1, -1);
            }

            // Handle the conversion more carefully
            // We need to convert single quotes to double quotes, but not within strings
            let result = '';
            let inString = false;
            let escapeNext = false;

            for (let i = 0; i < jsonString.length; i++) {
                const char = jsonString[i];
                const nextChar = jsonString[i + 1];

                if (escapeNext) {
                    result += char;
                    escapeNext = false;
                    continue;
                }

                if (char === '\\') {
                    result += char;
                    escapeNext = true;
                    continue;
                }

                if (char === "'") {
                    if (!inString) {
                        // We're starting a string, convert to double quote
                        result += '"';
                        inString = true;
                    } else {
                        // We're ending a string, but check if it's escaped
                        result += '"';
                        inString = false;
                    }
                } else if (char === '"' && inString) {
                    // Escape existing double quotes within strings
                    result += '\\"';
                } else {
                    result += char;
                }
            }

            // Wrap in braces if not already wrapped
            if (!result.trim().startsWith('{')) {
                result = `{${result}}`;
            }

            return JSON.parse(result);
        } catch (parseError) {
            // If all parsing fails, try a simpler approach
            try {
                // Extract key-value pairs manually
                const obj = {};
                const pairs = str.match(/'([^']+)':\s*'([^']*(?:\\'[^']*)*)'/g);

                if (pairs) {
                    pairs.forEach(pair => {
                        const match = pair.match(/'([^']+)':\s*'(.*)'/);
                        if (match) {
                            const key = match[1];
                            const value = match[2].replace(/\\'/g, "'"); // Unescape single quotes
                            obj[key] = value;
                        }
                    });
                    return obj;
                }
            } catch (manualError) {
                console.warn('Failed to parse tool parameters:', str, parseError, manualError);
            }

            // Return the original string if all parsing fails
            return str;
        }
    }
};

export const detectAndExtractTools = (message) => {
    const detectedTools = [];
    let cleanedMessage = message;

    // Find all potential tool starts
    const toolStarts = [];
    let index = 0;
    while ((index = message.indexOf('//', index)) !== -1) {
        toolStarts.push(index);
        index += 2;
    }

    toolStarts.forEach(startIndex => {
        const remainingMessage = message.substring(startIndex);

        // Extract tool name
        const toolNameMatch = remainingMessage.match(/^\/\/(\w+)/);
        if (!toolNameMatch) return;

        const toolName = toolNameMatch[1];

        // Check if this tool exists in our tools array
        const tool = agentBuilderTools.find(t =>
            t.usage.includes(`//${toolName}`)
        );

        if (!tool) return;

        // Find the complete tool string
        let toolString = `//${toolName}`;
        let parsedParams = null;

        // Check if it has parameters (starts with :)
        if (remainingMessage.charAt(toolString.length) === ':') {
            const paramStart = startIndex + toolString.length + 1;

            if (message.charAt(paramStart) === '{') {
                // Find matching closing brace
                let braceCount = 0;
                let paramEnd = paramStart;

                for (let i = paramStart; i < message.length; i++) {
                    if (message[i] === '{') braceCount++;
                    if (message[i] === '}') {
                        braceCount--;
                        if (braceCount === 0) {
                            paramEnd = i;
                            break;
                        }
                    }
                }

                toolString = message.substring(startIndex, paramEnd + 1);

                // Extract and parse parameters
                const paramString = message.substring(paramStart, paramEnd + 1);
                parsedParams = parseJsonLikeString(paramString);
            } else if (message.charAt(paramStart) === "'") {
                // Handle case where parameters start with single quote (like your example)
                let quoteCount = 0;
                let paramEnd = paramStart;

                for (let i = paramStart; i < message.length; i++) {
                    if (message[i] === "'" && message[i-1] !== '\\') {
                        quoteCount++;
                        if (quoteCount >= 2) { // Found opening and closing quotes
                            // Continue until we find the end of the parameter object
                            let braceLevel = 0;
                            let inQuotes = false;

                            for (let j = paramStart; j < message.length; j++) {
                                const char = message[j];
                                if (char === "'" && message[j-1] !== '\\') {
                                    inQuotes = !inQuotes;
                                }
                                if (!inQuotes) {
                                    if (char === '{') braceLevel++;
                                    if (char === '}') {
                                        braceLevel--;
                                        if (braceLevel === 0) {
                                            paramEnd = j;
                                            break;
                                        }
                                    }
                                }
                            }
                            break;
                        }
                    }
                }

                toolString = message.substring(startIndex, paramEnd + 1);
                const paramString = message.substring(paramStart, paramEnd + 1);
                parsedParams = parseJsonLikeString(paramString);
            } else {
                // Handle non-JSON parameters (like plain text after :)
                const restOfLine = remainingMessage.substring(toolString.length + 1);
                const lineEnd = restOfLine.indexOf('\n');
                const paramString = lineEnd !== -1 ? restOfLine.substring(0, lineEnd) : restOfLine;
                toolString += ':' + paramString;
                parsedParams = paramString.trim();
            }
        }

        detectedTools.push({
            name: tool.name,
            key: tool.key,
            run: toolString,
            params: parsedParams
        });

        // Remove from cleaned message
        cleanedMessage = cleanedMessage.replace(toolString, '').trim();
    });

    return {
        cleanedMessage,
        detectedTools
    };
}

export function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}
